// Google Analytics utility functions for conversion tracking

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Track page views
export const trackPageView = (page_title, page_location) => {
  if (isGtagAvailable()) {
    window.gtag('config', 'AW-17426701302', {
      page_title: page_title,
      page_location: page_location
    });
  }
};

// Track user signup attempts
export const trackSignup = (method = 'email') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'sign_up', {
      method: method,
      custom_parameter: 'tyme_signup'
    });
  }
};

// Track account creation completion (after email verification)
export const trackAccountCreated = (plan = 'starter') => {
  if (isGtagAvailable()) {
    // Track as signup completion event
    window.gtag('event', 'account_created', {
      plan: plan,
      custom_parameter: 'tyme_account_verified'
    });
    
    // Track as a conversion for Google Ads
    window.gtag('event', 'conversion', {
      send_to: 'AW-17426701302/account_created',
      value: 1.0,
      currency: 'GBP'
    });
  }
};

// Track plan selection/purchase clicks
export const trackPlanSelection = (plan_name, plan_price) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'begin_checkout', {
      currency: 'GBP',
      value: plan_price,
      items: [{
        item_id: plan_name.toLowerCase().replace(' ', '_'),
        item_name: plan_name,
        category: 'subscription_plan',
        price: plan_price,
        quantity: 1
      }]
    });
    
    // Track as purchase intent conversion
    window.gtag('event', 'conversion', {
      send_to: 'AW-17426701302/purchase_intent',
      value: plan_price,
      currency: 'GBP'
    });
  }
};

// Track demo/trial requests
export const trackDemoRequest = () => {
  if (isGtagAvailable()) {
    window.gtag('event', 'generate_lead', {
      currency: 'GBP',
      value: 50.0, // Estimated lead value
      custom_parameter: 'demo_request'
    });
    
    window.gtag('event', 'conversion', {
      send_to: 'AW-17426701302/demo_request',
      value: 50.0,
      currency: 'GBP'
    });
  }
};

// Track user login success
export const trackLogin = (method = 'email') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'login', {
      method: method
    });
  }
};

// Track form submissions
export const trackFormSubmission = (form_name, form_id) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'form_submit', {
      form_name: form_name,
      form_id: form_id
    });
  }
};

// Track engagement events
export const trackEngagement = (event_name, engagement_time_seconds = null) => {
  if (isGtagAvailable()) {
    const eventData = { custom_parameter: event_name };
    if (engagement_time_seconds) {
      eventData.engagement_time_msec = engagement_time_seconds * 1000;
    }
    
    window.gtag('event', 'engagement', eventData);
  }
};

// Track attendance system usage (key business metric)
export const trackAttendanceAction = (action_type, location_id) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'attendance_action', {
      custom_parameter: action_type,
      location_id: location_id,
      event_category: 'attendance_system',
      event_label: `${action_type}_${location_id}`
    });
    
    // Track successful check-ins as micro-conversions
    if (action_type === 'check_in' || action_type === 'check_out') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-17426701302/attendance_usage',
        value: 0.1,
        currency: 'GBP'
      });
    }
  }
};

// Track error events (for optimization)
export const trackError = (error_type, error_message) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'exception', {
      description: `${error_type}: ${error_message}`,
      fatal: false
    });
  }
};

// Track feature usage for product insights
export const trackFeatureUsage = (feature_name, feature_category = 'general') => {
  if (isGtagAvailable()) {
    window.gtag('event', 'feature_usage', {
      custom_parameter: feature_name,
      event_category: feature_category,
      event_label: feature_name
    });
  }
};

// Track time spent on key pages
export const trackTimeOnPage = (page_name, time_seconds) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'timing_complete', {
      name: 'page_view_time',
      value: time_seconds * 1000, // Convert to milliseconds
      event_category: 'User Engagement',
      event_label: page_name
    });
  }
};

// Track completed subscription purchase (conversion)
export const trackPurchase = (plan_name, plan_price, transaction_id = null) => {
  if (isGtagAvailable()) {
    // Track as purchase event
    window.gtag('event', 'purchase', {
      transaction_id: transaction_id || `txn_${Date.now()}`,
      value: plan_price,
      currency: 'GBP',
      items: [{
        item_id: plan_name.toLowerCase().replace(' ', '_'),
        item_name: plan_name,
        category: 'subscription_plan',
        price: plan_price,
        quantity: 1
      }]
    });
    
    // Track as Google Ads conversion
    window.gtag('event', 'conversion', {
      send_to: 'AW-17426701302/purchase_complete',
      value: plan_price,
      currency: 'GBP',
      transaction_id: transaction_id || `txn_${Date.now()}`
    });
  }
};

export default {
  trackPageView,
  trackSignup,
  trackAccountCreated,
  trackPlanSelection,
  trackPurchase,
  trackDemoRequest,
  trackLogin,
  trackFormSubmission,
  trackEngagement,
  trackAttendanceAction,
  trackError,
  trackFeatureUsage,
  trackTimeOnPage
};